import { AnimationEvent, Dispatch, SetStateAction, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';

import FlyoutMenu from '@/components/header/FlyoutMenu';
import {
	leftNavItems,
	rightNavItems,
	SCREENING_NAV_ITEM,
} from '@/components/homepageRefresh/header/NavItems/data';
import { useUserEligibility } from '@/hooks/user/useUserEligibility';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import messages from '@/messages/messages';

import NavItemLink from '../NavItemLink';
import { HeaderNavItem } from '../NavItemLink/types';
import _messages from './messages';
import {
	BackButton,
	BackChevron,
	ListViewport,
	MobileNavWrapper,
	SlideFrom,
	SubmenuTrigger,
	SubmenuTriggerChevron,
} from './styles';

const MobileMenu = ({
	isMobileMenuOpen,
	setIsMobileMenuOpen,
	onNavItemClick,
	onLoginClick,
}: {
	isMobileMenuOpen: boolean;
	setIsMobileMenuOpen: Dispatch<SetStateAction<boolean>>;
	onNavItemClick: (navItem: HeaderNavItem) => Promise<void>;
	onLoginClick: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { isLoggedOut, isLoggedInValid } = useUserEligibility();
	const { isEnrolled } = useAmplitudeExperiment('www-screening-header-nav');

	const mobileNavItems: HeaderNavItem[] = [
		...leftNavItems,
		...(isEnrolled ? [SCREENING_NAV_ITEM, ...rightNavItems] : rightNavItems),
	];

	const [activeSubmenu, setActiveSubmenu] = useState<HeaderNavItem | null>(null);
	// Which edge the incoming list is sliding in from; null once it has arrived.
	const [slideFrom, setSlideFrom] = useState<SlideFrom | null>(null);
	const [wasMenuOpen, setWasMenuOpen] = useState(isMobileMenuOpen);
	const backButtonRef = useRef<HTMLButtonElement>(null);
	const submenuTriggerRefs = useRef(new Map<HeaderNavItem, HTMLButtonElement>());

	// MobileMenu stays mounted while the flyout is closed (it just slides off-screen), so
	// its state survives a close. wasMenuOpen remembers the last isMobileMenuOpen we saw:
	// when the menu has just opened, start again from the main list, with no slide.
	// Resetting on open rather than on close avoids swapping lists while the panel slides away.
	if (isMobileMenuOpen !== wasMenuOpen) {
		setWasMenuOpen(isMobileMenuOpen);
		if (isMobileMenuOpen) {
			setActiveSubmenu(null);
			setSlideFrom(null);
		}
	}

	// Each list unmounts the button that swapped it out, so focus is moved
	// explicitly or it would drop to the page.
	const openSubmenu = (navItem: HeaderNavItem) => {
		flushSync(() => {
			setSlideFrom('right');
			setActiveSubmenu(navItem);
		});
		backButtonRef.current?.focus();
	};

	const closeSubmenu = () => {
		const submenu = activeSubmenu;
		flushSync(() => {
			setSlideFrom('left');
			setActiveSubmenu(null);
		});
		if (submenu) {
			submenuTriggerRefs.current.get(submenu)?.focus();
		}
	};

	// When the slide finishes, stop clipping the list. animationend bubbles, so the
	// target check ignores animations finishing on elements inside the list.
	const onSlideEnd = (event: AnimationEvent<HTMLUListElement>) => {
		if (event.target === event.currentTarget) {
			setSlideFrom(null);
		}
	};

	const onMobileLinkClick = async (navItem: HeaderNavItem) => {
		setIsMobileMenuOpen(false);
		await onNavItemClick(navItem);
	};

	const renderSubmenuItems = (submenuItems: NonNullable<HeaderNavItem['items']>) =>
		submenuItems.map(item => (
			<li key={item.href}>
				<NavItemLink navItem={item} onClick={() => onMobileLinkClick(item)} isOpaque={false} />
			</li>
		));

	const renderMainItems = () => (
		<>
			{mobileNavItems.map(navItem => (
				<li key={formatMessage(navItem.label)}>
					{navItem.items ? (
						<SubmenuTrigger
							ref={element => {
								if (element) {
									submenuTriggerRefs.current.set(navItem, element);
								}
								return () => {
									submenuTriggerRefs.current.delete(navItem);
								};
							}}
							type="button"
							tabIndex={0}
							onClick={() => openSubmenu(navItem)}
						>
							{formatMessage(navItem.label)}
							<SubmenuTriggerChevron aria-hidden />
						</SubmenuTrigger>
					) : (
						<NavItemLink navItem={navItem} onClick={() => onMobileLinkClick(navItem)} isOpaque={false} />
					)}
				</li>
			))}
			{isLoggedOut && (
				<li>
					<NavItemLink
						ariaLabel="Log In"
						isOpaque={false}
						onClick={onLoginClick}
						navItem={{ label: messages.loginButton }}
					/>
				</li>
			)}
			{!isLoggedOut && !isLoggedInValid && (
				<li>
					<NavItemLink
						isOpaque={false}
						onClick={() => onNavItemClick({ label: messages.continueToApp, href: '/app' })}
						navItem={{ label: messages.continueToApp, href: '/app' }}
					/>
				</li>
			)}
			{!isLoggedOut && (
				<li>
					<NavItemLink
						isOpaque={false}
						onClick={() => onNavItemClick({ label: messages.profile, href: '/app/profile' })}
						navItem={{ label: messages.profile, href: '/app/profile' }}
					/>
				</li>
			)}
		</>
	);

	return (
		<FlyoutMenu isOpen={isMobileMenuOpen} label="Mobile navigation menu">
			{activeSubmenu?.items && (
				<BackButton
					ref={backButtonRef}
					type="button"
					tabIndex={0}
					aria-label={formatMessage(_messages.backToMainMenu)}
					onClick={closeSubmenu}
				>
					<BackChevron aria-hidden />
				</BackButton>
			)}
			{/* A new key on each switch mounts the incoming list fresh, so its slide plays. */}
			<ListViewport key={activeSubmenu?.items ? 'submenu' : 'main'} $isSliding={slideFrom !== null}>
				<MobileNavWrapper
					$slideFrom={slideFrom}
					onAnimationEnd={onSlideEnd}
					aria-label={activeSubmenu?.items ? formatMessage(activeSubmenu.label) : undefined}
				>
					{activeSubmenu?.items ? renderSubmenuItems(activeSubmenu.items) : renderMainItems()}
				</MobileNavWrapper>
			</ListViewport>
		</FlyoutMenu>
	);
};

export default MobileMenu;
